// Styles
import './ShippingRequest.scss';

// Libs
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Common
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
// Components
import TextAreaField from '@/common/components/molecules/TextAreaField/TextAreaField';
// Constants
import { ITEM_TYPE_PRODUCT } from '@/common/constants';
// Contexts
import { useAuth } from '@/common/contexts/AuthContext';
// Hook
import useShippingRequest from '@/common/hooks/orders/useShippingRequest';

import {
  ShippingRequestDefaultProps,
  ShippingRequestPropTypes,
} from './ShippingRequest.proptypes';

const ShippingRequest = ({
  disabled,
  isLoading,
  items,
  orderId,
  reference,
  refetchOrder,
  showWarning,
  type,
}) => {
  const { t } = useTranslation();
  const { zergRush } = useAuth();
  const {
    isDisabled: isDisabledUseShippingRequest,
    onShippingRequest,
  } = useShippingRequest({ disabled, isLoading });

  const isDisabled = useMemo(() => {
    const blocked =
      zergRush || disabled || isDisabledUseShippingRequest.disabled; // TODO: ACL zergRush to check
    const loading = isLoading || isDisabledUseShippingRequest.loading;

    return {
      loading,
      disabled: blocked,
    };
  }, [disabled, isDisabledUseShippingRequest, isLoading, zergRush]);

  /**
   * Return list of selected items to ship
   * @type {*}
   */
  const selectedItems = useMemo(
    () =>
      items
        .filter(
          (item) =>
            item.is_selected &&
            // TODO: Remove .toLowerCase > Waiting for a dev: Cleaning CAPS in type
            item.type.toLowerCase() === ITEM_TYPE_PRODUCT &&
            item.quantity_selected > 0,
        )
        .map((item) => ({
          id: item.id,
          quantity: item.quantity_selected,
        })),
    [items],
  );

  /**
   * create a delivery request with a condition on the item selections
   * if the items are empty set the hasNoSelection key to true
   * @param {object} values of comment
   * @param {object} actions from formik
   * @type {(function(object, object): void)|*}
   */
  const onCreateShippingRequest = useCallback(
    (values, actions) => {
      if (selectedItems.length > 0) {
        onShippingRequest({
          orderId,
          items: selectedItems,
          comment: values.comment,
          actions,
          onSuccess: refetchOrder,
        });
      } else {
        onShippingRequest({
          orderId,
          hasNoSelection: true,
          comment: values.comment,
          actions,
        });
      }
    },
    [onShippingRequest, orderId, refetchOrder, selectedItems],
  );

  return (
    <div
      className="shipping-request__content"
      data-qa-id="modal-shipping-request"
    >
      <h1 className="rs-modal-title">
        {t(`ebat:entities.orders.modal.${type}.title`)}
      </h1>
      <div className="shipping-request__description">
        <p>{t(`ebat:entities.orders.modal.${type}.description`)}</p>
        {showWarning && (
          <p className="error">
            {t(`ebat:entities.orders.modal.${type}.warning`)}
          </p>
        )}
      </div>
      {reference && (
        <div className="shipping-request__reference">
          <p className="label">{t('common:messages:forms.fields.reference')}</p>
          <p className="value">{reference}</p>
        </div>
      )}
      <div className="shipping-request__wrapper">
        <Formik
          initialValues={{
            comment: '',
          }}
          onSubmit={async (values, actions) =>
            onCreateShippingRequest(values, actions)
          }
        >
          {({ errors, touched }) => (
            <Form className="form" data-qa-id="shipping-request-form">
              <Field
                as={TextAreaField}
                context="comment-field"
                disabled={isDisabled.disabled}
                errors={
                  errors.comment &&
                  touched.comment && [
                    t(`common:messages:forms.validation.${errors.comment}`),
                  ]
                }
                id="comment"
                name="comment"
              >
                {t('common:messages:forms.fields.comment')}
              </Field>
              <ButtonLoading
                context="shipping-request-save"
                disabled={isDisabled.disabled}
                isLoading={isDisabled.loading}
              >
                {t('common:messages:actions.valid')}
              </ButtonLoading>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

ShippingRequest.defaultProps = {
  ...ShippingRequestDefaultProps,
};

ShippingRequest.propTypes = {
  ...ShippingRequestPropTypes,
};

export default ShippingRequest;
